import React, { useEffect, useMemo, useState } from 'react';
import Layout from '../../components/Layout';
import ApiInspector from '../../components/ApiInspector';
import { apiFetch } from '../../lib/api';
import { useFiltroSocieta } from '../../hooks/useFiltroSocieta';
import { formatQuantity, formatCurrency } from '../../utils/formatters';
import { API_DOCS } from './GiacenzePage.api';

interface Deposito {
  id: number;
  nome: string;
  tipo: string;
}

/**
 * Etichetta compatta per un deposito — vedi MovimentiMagazzinoPage per i dettagli.
 */
const DEP_TAG: Record<string, string> = { FISSO: 'FISSO', CONTO_TERZI: 'CT', COMMESSA: 'COM' };
function depositoLabel(d: { nome: string; tipo: string } | null | undefined): string {
  if (!d) return '';
  let nome = d.nome;
  if (d.tipo === 'COMMESSA' && nome.startsWith('Commessa: ')) nome = nome.slice('Commessa: '.length);
  if (nome.length > 32) nome = nome.slice(0, 31).trimEnd() + '…';
  const tag = DEP_TAG[d.tipo] || d.tipo;
  return `${nome} [${tag}]`;
}

interface Giacenza {
  id: number;
  societaId: number;
  deposito: { id: number; nome: string; tipo: string } | null;
  articolo: { id: number; codice: string; descrizioneBase: string; tipologia: string } | null;
  quantita: number | string;
  cmp: number | string;
  updatedAt: string;
}

export default function GiacenzePage() {
  const { societaId, setSocietaId, listaSocieta, isUnica, pronta } = useFiltroSocieta();

  const [items, setItems] = useState<Giacenza[]>([]);
  const [depositi, setDepositi] = useState<Deposito[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filtroDeposito, setFiltroDeposito] = useState<string>('');
  const [soloMovimentati, setSoloMovimentati] = useState(true);
  const [ricerca, setRicerca] = useState('');

  const loadDepositi = async () => {
    if (!societaId) { setDepositi([]); return; }
    try {
      // @api GET /api/depositi?societaId= -> { depositi: [{ id, nome, tipo }] }
      const res = await apiFetch<{ depositi: Deposito[] }>(`/api/depositi?societaId=${societaId}`);
      setDepositi(res.depositi ?? []);
    } catch {
      setDepositi([]);
    }
  };

  const load = async () => {
    if (!pronta) return;
    if (!societaId) { setItems([]); setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      const qs = new URLSearchParams({ societaId: String(societaId) });
      if (filtroDeposito)  qs.set('depositoId', filtroDeposito);
      if (soloMovimentati) qs.set('soloMovimentati', 'true');
      // @api GET /api/giacenze?societaId=&depositoId=&soloMovimentati= -> { success: true, data: [{ id, deposito, articolo, quantita, cmp, updatedAt }] }
      const res = await apiFetch<{ success: boolean; data: Giacenza[] }>(`/api/giacenze?${qs.toString()}`);
      setItems(res.data ?? []);
    } catch (e: any) {
      setError(e.message || 'Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadDepositi(); }, [societaId]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [societaId, pronta, filtroDeposito, soloMovimentati]);

  const filtered = useMemo(() => {
    const q = ricerca.trim().toLowerCase();
    if (!q) return items;
    return items.filter(g =>
      (g.articolo?.codice || '').toLowerCase().includes(q)
      || (g.articolo?.descrizioneBase || '').toLowerCase().includes(q)
    );
  }, [items, ricerca]);

  const noSocieta = !societaId;

  return (
    <Layout>
      <div className="bop-page-header">
        <div>
          <h2 className="bop-page-title">Giacenze di magazzino</h2>
          <p className="bop-text-muted">
            Snapshot articolo × deposito aggiornato a ogni movimento confermato. CMP = costo medio ponderato corrente.
          </p>
        </div>
      </div>

      <div className="bop-filter-bar">
        {!isUnica && (
          <select className="bop-select" value={societaId} onChange={e => setSocietaId(e.target.value)}>
            <option value="">Seleziona società…</option>
            {listaSocieta.map(s => <option key={s.id} value={s.id}>{s.nome}</option>)}
          </select>
        )}
        <input
          className="bop-input"
          placeholder="Cerca per codice o descrizione articolo…"
          value={ricerca}
          onChange={e => setRicerca(e.target.value)}
        />
        <select className="bop-select bop-filter-bar__fill" value={filtroDeposito} onChange={e => setFiltroDeposito(e.target.value)}>
          <option value="">Tutti i depositi</option>
          {depositi.map(d => <option key={d.id} value={d.id}>{depositoLabel(d)}</option>)}
        </select>
        <label className="bop-flex">
          <input type="checkbox" checked={soloMovimentati} onChange={e => setSoloMovimentati(e.target.checked)} />
          <span>&nbsp;Solo articoli movimentati</span>
        </label>
      </div>

      {error && <div className="bop-inline-error">{error}</div>}

      <div className="bop-card">
        {noSocieta ? (
          <div className="bop-empty-state">Seleziona una società per visualizzare le giacenze.</div>
        ) : loading ? (
          <div className="bop-empty-state">Caricamento…</div>
        ) : filtered.length === 0 ? (
          <div className="bop-empty-state">
            {soloMovimentati
              ? 'Nessuna giacenza movimentata. Le righe verranno popolate dai movimenti di magazzino confermati.'
              : 'Nessuna giacenza presente.'}
          </div>
        ) : (
          <table className="bop-table">
            <thead>
              <tr>
                <th>Articolo</th>
                <th>Tipologia</th>
                <th>Deposito</th>
                <th className="bop-text-right">Quantità</th>
                <th className="bop-text-right">CMP (€)</th>
                <th className="bop-text-right">Valore (€)</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(g => {
                const qta = Number(g.quantita);
                const cmp = Number(g.cmp);
                const val = qta * cmp;
                return (
                  <tr key={g.id}>
                    <td>
                      {g.articolo ? (
                        <>
                          <strong>{g.articolo.codice}</strong>
                          <div className="bop-text-muted">{g.articolo.descrizioneBase}</div>
                        </>
                      ) : <span className="bop-text-muted">—</span>}
                    </td>
                    <td><span className="bop-text-muted">{g.articolo?.tipologia ?? '—'}</span></td>
                    <td>{g.deposito ? depositoLabel(g.deposito) : '—'}</td>
                    <td className="bop-text-right">{formatQuantity(qta)}</td>
                    <td className="bop-text-right">{formatCurrency(cmp)}</td>
                    <td className="bop-text-right"><strong>{formatCurrency(val)}</strong></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <ApiInspector docs={API_DOCS} />
    </Layout>
  );
}
